import { getApiUrl } from '../../config/api';
import { projectService } from '../../services/projectService';
import { MediaItem, UploadResult } from '../../types';
import { uploadProjectMedia } from '../../services/mediaService';
import { saveMediaItemToJson } from './mediaItemService';

/**
 * Fetch project details by ID
 */
export const fetchProjectDetails = async (projectId: string) => {
  try {
    const projectData = await projectService.getProject(projectId);
    return projectData;
  } catch (error) {
    console.error("Error fetching project details:", error);
    return null;
  }
};

/**
 * Update project title
 */
export const handleUpdateTitle = async (
  newTitle: string,
  projectId: string,
  showNotification: (notification: any) => void,
  setProjectTitle: React.Dispatch<React.SetStateAction<string>>,
  setIsEditingTitle: React.Dispatch<React.SetStateAction<boolean>>,
  setDisabledBtnPendingDownload: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (!projectId || !newTitle.trim()) return;

  try {
    setDisabledBtnPendingDownload(true);

    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(getApiUrl(`/projects/${projectId}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ title: newTitle }),
    });

    if (!response.ok) {
      throw new Error('Failed to update project title');
    }

    setProjectTitle(newTitle);
    setIsEditingTitle(false);
    //showNotification({ type: 'success', message: 'Project title updated successfully!', title: 'Success' });
  } catch (error) {
    showNotification({ type: 'error', message: 'Failed to update project title', title: 'Error' });
    const projectData = await fetchProjectDetails(projectId);
    if (projectData?.title) {
      setProjectTitle(projectData.title);
    }
  } finally {
    setDisabledBtnPendingDownload(false);
  }
};

/**
 * Update project description
 */
export const handleUpdateDescription = async (
  newDescription: string,
  projectId: string,
  showNotification: (notification: any) => void,
  setProjectDescription: React.Dispatch<React.SetStateAction<string>>,
  setDisabledBtnPendingDownload: React.Dispatch<React.SetStateAction<boolean>>
) => {
  console.log('🚀 handleUpdateDescription called:', { newDescription, projectId });
  
  if (!projectId) {
    console.warn('⚠️ No projectId provided');
    return;
  }

  try {
    setDisabledBtnPendingDownload(true);

    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(getApiUrl(`/projects/${projectId}`), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ description: newDescription }),
    });

    console.log('📡 API response:', response.status);

    if (!response.ok) {
      throw new Error('Failed to update project description');
    }

    setProjectDescription(newDescription);
    //showNotification({ type: 'success', message: 'Project description updated!', title: 'Success' });
    console.log('✅ Description updated successfully');
  } catch (error) {
    console.error('❌ Error updating description:', error);
    showNotification({ type: 'error', message: 'Failed to update description', title: 'Error' });
    const projectData = await fetchProjectDetails(projectId);
    if (projectData?.description) {
      setProjectDescription(projectData.description);
    }
  } finally {
    setDisabledBtnPendingDownload(false);
  }
};

/**
 * Save media files to S3
 * ✅ UPDATED: Now revokes blob URLs after successful upload
 */
export const handleSaveMedia = async (
  mediaItems: MediaItem[],
  projectId: string,
  audioUrl: string | null
): Promise<MediaItem[] | undefined> => {
  // ✅ FIXED: Allow saving even without audioUrl (video-only mode)
  // Removed: if (!audioUrl) return;

  try {
    const tempMediaFiles = mediaItems.filter(
      (item) => item.url ? !item.url.includes('amazonaws.com') : !item.src?.includes('amazonaws.com')
    );
    
    if (tempMediaFiles.length === 0) return mediaItems;

    // ✅ Track blob URLs that need to be revoked after upload
    const blobUrlsToRevoke: string[] = [];
    
    const files = tempMediaFiles.map(async (item) => {
      // Track blob URLs for cleanup
      if (item.url && item.url.startsWith('blob:')) {
        blobUrlsToRevoke.push(item.url);
      }
      if (item.src && item.src.startsWith('blob:')) {
        blobUrlsToRevoke.push(item.src);
      }
      
      const response = await fetch(item.url.split("?")[0], { method: 'GET' });
      const blob = await response.blob();
      return new File([blob], item.name, { type: blob.type });
    });

    const fileArray = await Promise.all(files);
    const uploadResult: UploadResult = await uploadProjectMedia(fileArray, projectId, audioUrl || ''); // Use empty string as fallback

    const updatedItems = mediaItems.map((item) => {
      if (item.url?.includes('amazonaws.com') || item.src?.includes('amazonaws.com')) {
        return item;
      }
      
      const uploadedFileIndex = tempMediaFiles.findIndex(tempItem => tempItem.id === item.id);
      
      if (uploadedFileIndex !== -1 && uploadResult.urls[uploadedFileIndex]) {
        return {
          ...item,
          url: uploadResult.urls[uploadedFileIndex],
          src: uploadResult.urls[uploadedFileIndex]
        };
      }
      
      return item;
    });

    // ✅ CRITICAL: Revoke all blob URLs after successful upload
    console.log(`🧹 Revoking ${blobUrlsToRevoke.length} blob URLs after successful upload`);
    blobUrlsToRevoke.forEach(blobUrl => {
      try {
        URL.revokeObjectURL(blobUrl);
        console.log(`✅ Revoked blob URL: ${blobUrl.substring(0, 50)}...`);
      } catch (error) {
        console.warn(`⚠️ Failed to revoke blob URL: ${error}`);
      }
    });

    return updatedItems;
  } catch (error) {
    console.error("Failed to save media items:", error);
    return mediaItems;
  }
};

/**
 * Save media items to JSON on S3
 */
export const handleSaveMediaTextToJson = async (
  projectId: string,
  audioUrl: string | null,
  mediaItems: MediaItem[],
  showNotification: (notification: any) => void,
  setIsProcessing: React.Dispatch<React.SetStateAction<any>>,
  setDisabledBtnPendingDownload: React.Dispatch<React.SetStateAction<boolean>>,
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>
) => {
  // ✅ FIXED: Allow saving even without audioUrl (video-only mode)
  // if (!audioUrl) return;

  try {
    setIsProcessing((prev: any) => ({ ...prev, savingMedia: true }));
    setDisabledBtnPendingDownload(true);

    const updatedItems = await handleSaveMedia(mediaItems, projectId, audioUrl);
    if (updatedItems) {
      setMediaItems(updatedItems);
    }
    await saveMediaItemToJson(projectId, updatedItems!);

    //showNotification({ type: 'success', message: 'Media items saved successfully!', title: 'Success' });
    return updatedItems;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    showNotification({ type: 'error', message: `Failed to save media items! ${errorMessage}`, title: 'Error' });
  } finally {
    setIsProcessing((prev: any) => ({ ...prev, savingMedia: false }));
    setDisabledBtnPendingDownload(false);
  }
};